import React, {useEffect, useMemo, useRef, useState} from 'react';

import {useSystemAccounts} from 'src/hooks/account/system-accounts';
import {useInitiatorSystems} from 'src/hooks/dictionaries/useInitiatorSystems';
import {runUserSearch} from 'src/openapi/admin/apis/UserSearchApi';
import {SortDirection} from 'src/openapi/admin/models/SortDirection';
import {UserSortField} from 'src/openapi/admin/models/UserSortField';
import {useAppDispatch} from 'src/store/hooks';

import {Autosuggest, AutosuggestItem, AutosuggestListCaption} from '@pb/kit-component-autosuggest-v2';
import {FlatButton} from '@pb/kit-component-button';

import type {UserSearchResult} from 'src/openapi/admin/models/UserSearchResult';

type InitiatorUser = Pick<UserSearchResult, 'id' | 'email'> & {kind: 'user'};
type InitiatorSystem = {kind: 'system', system: string};
export type InitiatorValue = InitiatorSystem | InitiatorUser;
type Props = {value?: InitiatorValue, onChange: (value: InitiatorValue | undefined) => void};
type Result = {query: string, users?: InitiatorUser[], error?: boolean};
const MAX_RESULTS = 10;
const caption = (value?: InitiatorValue) => value?.kind === 'user' ? value.email : value?.system ?? '';

export const InitiatorSuggest = ({value, onChange}: Props) => {
    const dispatch = useAppDispatch();
    const {adminAccountIds} = useSystemAccounts();
    const {initiatorSystems, status, loadInitiatorSystems} = useInitiatorSystems();
    const [result, setResult] = useState<Result | null>(null);
    const [draft, setDraft] = useState(() => caption(value));
    const [editing, setEditing] = useState(false);
    const [shown, setShown] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const email = draft.trim();
    const canSearch = editing && email.length >= 3;
    const systems = useMemo(() => {
        const query = email.toLowerCase();
        const relevance = (code: string) => {
            const name = code.toLowerCase();
            if (name === query) {
                return 0;
            }
            return name.startsWith(query) ? 1 : 2;
        };
        return initiatorSystems
            .filter(item => item.code.toLowerCase().includes(query))
            .sort((left, right) => relevance(left.code) - relevance(right.code)
                || left.code.localeCompare(right.code))
            .slice(0, MAX_RESULTS)
            .map(({code}): InitiatorSystem => ({kind: 'system', system: code}));
    }, [email, initiatorSystems]);

    useEffect(() => {
        setDraft(caption(value));
        setEditing(false);
    }, [value]);

    useEffect(() => {
        setResult(null);
        if (!canSearch || !adminAccountIds.length) {
            return;
        }
        const controller = new AbortController();
        const timer = setTimeout(async () => {
            try {
                const {payload} = await dispatch(runUserSearch({
                    userSearchRequest: {
                        searchCriteria: {accountIds: adminAccountIds, userEmails: [email]},
                        userSortCriteria: {columnId: UserSortField.EMAIL, direction: SortDirection.ASC},
                        pagination: {currentPage: 1, pageSize: MAX_RESULTS},
                    },
                }, {signal: controller.signal}));
                if (!controller.signal.aborted) {
                    setResult(payload.success
                        ? {query: email, users: payload.data.result.slice(0, MAX_RESULTS)
                                .map(user => ({kind: 'user', id: user.id, email: user.email}))}
                        : {query: email, error: true});
                }
            } catch {
                if (!controller.signal.aborted) {
                    setResult({query: email, error: true});
                }
            }
        }, 1000);
        return () => {
            clearTimeout(timer);
            controller.abort();
        };
    }, [adminAccountIds, canSearch, dispatch, email]);

    const current = canSearch && result?.query === email ? result : null;
    let suggestions: React.ReactNode = null;
    if (canSearch) {
        if (!adminAccountIds.length || current?.error) {
            suggestions = <div className="pd-s">Unable to suggest users.</div>;
        } else if (!current) {
            suggestions = <div className="pd-s">Loading</div>;
        } else if (!current.users?.length) {
            suggestions = <div className="pd-s">No users found</div>;
        } else {
            suggestions = current.users.map(user => (
                <AutosuggestItem key={user.id} value={user} caption={user.email}/>
            ));
        }
    }
    let systemSuggestions: React.ReactNode;
    if (status === 'failed') {
        systemSuggestions = (
            <div className="pd-s">
                <div>Unable to load systems.</div>
                <FlatButton
                    type="button"
                    onMouseDown={event => event.preventDefault()}
                    onClick={() => {
                        loadInitiatorSystems();
                    }}>
                    Retry systems
                </FlatButton>
            </div>
        );
    } else if (status !== 'succeeded') {
        systemSuggestions = <div className="pd-s">Loading systems</div>;
    } else if (!systems.length) {
        systemSuggestions = <div className="pd-s">No systems found</div>;
    } else {
        systemSuggestions = systems.map(system => (
            <AutosuggestItem key={system.system} value={system} caption={system.system}/>
        ));
    }
    return (
        <Autosuggest
            ref={inputRef}
            id="provisioning-initiator"
            value={draft}
            placeholder="Search by initiator system or user email"
            aria-describedby="provisioning-initiator-hint"
            isClosedOnClear={false}
            shown={shown}
            setShown={setShown}
            onFocus={() => {
                setShown(true);
                loadInitiatorSystems();
            }}
            onClick={() => setShown(true)}
            onBlur={() => {
                setShown(false);
                setDraft(caption(value));
                setEditing(false);
            }}
            onChange={event => {
                setDraft(event.currentTarget.value);
                setEditing(true);
            }}
            onItemSelect={(item: InitiatorValue) => {
                // Autosuggest can retain the highlighted item while a new query is loading.
                const available = item.kind === 'system' ? systems.includes(item) : current?.users?.includes(item);
                if (available) {
                    // Restore focus before closing so the list's return-focus event cannot reopen it.
                    inputRef.current?.focus();
                    setShown(false);
                    setDraft(caption(item));
                    setEditing(false);
                    onChange(item);
                }
            }}
            onClear={() => {
                setDraft('');
                setEditing(false);
                onChange(undefined);
            }}>
            <AutosuggestListCaption>Systems</AutosuggestListCaption>
            {systemSuggestions}
            {canSearch ? <AutosuggestListCaption>Users</AutosuggestListCaption> : null}
            {suggestions}
        </Autosuggest>
    );
};
